import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import { Checkbox } from '../components/ui/checkbox.js';
import { useCustomer } from '../customer-context.js';
import type { CustomerContext } from '../session.js';
import { useAgentTeller } from '../agent-teller.js';
import { useContent } from '../content.js';
import { api } from '../api/client.js';
import { setDuplicateSessionBlock } from '../duplicate-session-store.js';
import { setJnHoldBlock } from '../jn-hold-block-store.js';
import { mediumSecurityRouteFor } from '../routing/security-routing.js';
import infoIcon from '../assets/info-icon.svg';
import errorIcon from '../assets/error-icon.svg';
import { ArdowenLogo } from '../components/ArdowenLogo.js';

/** Card / info-box wrapper matching the PTSB design-system `InfoBox`. */
function InfoBox({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`bg-white border-2 border-[#dad7d3] p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

/** The PAN positions the gateway challenged (1-based). Falls back to 1/2/3 when
 * the gateway did not return them (the mock's fixed challenge). */
interface PanPositions {
  Position1: number;
  Position2: number;
  Position3: number;
}
const DEFAULT_PAN_POSITIONS: PanPositions = {
  Position1: 1,
  Position2: 2,
  Position3: 3,
};

/** Ordinal label for a 1-based position (1→"1st", 2→"2nd", 3→"3rd", 5→"5th"). */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

interface CardResult {
  found: boolean;
  message: string;
  /**
   * The gateway result/error code on an unsuccessful lookup (CCF-1). Surfaced
   * inline beside the Search action — e.g. a cancelled online card (9103) or any
   * `customer/identification` decline (issue #94, CCF-1 Unhappy Path #3). The
   * lookup is a non-blocking WARN: the form stays usable so the agent can try
   * another card number.
   */
  resultCode?: string;
  /**
   * CCF-1 (#163): set when the gateway returns code 1001 "card is not
   * registered". The card is unregistered, so the agent is offered a Register
   * affordance that routes to registration.
   */
  canRegister?: boolean;
  /**
   * #201 (ONLINE journey): set by the BFF when it confirmed SERVER-SIDE that the
   * IVR already verified the customer's PAN (`Verification=PAN`). The PAN
   * challenge is skipped and the screen routes to Medium Security.
   */
  ivrVerified?: boolean;
  /**
   * CCF-1 (#163 points 2-3, #234): present with `canRegister` when the
   * unregistered card RESOLVED to a customer via customer/identification. The
   * SPA puts this into the agent-banner context BEFORE routing to registration
   * so the registration screen has a CIF (otherwise it shows "No customer is in
   * context"). Absent ⇒ the Register button still routes to Open24 by default,
   * but with no context (unchanged fallback).
   */
  registrationCustomer?: {
    cif: string;
    name?: string;
    onlineCardNumber?: string;
    segment?: string;
    registrationType?: string;
    dob?: string;
    /**
     * #768 (CCF-3): Y/N CLI match for the registration path. `cardno/validate`
     * returns no CustomerNumber for an unregistered/de-registered card, so the
     * BFF computes the match against the resolved customer's registered mobile
     * here so the banner populates on the registration screen (not just after a
     * successful ID&V). Absent when no CLI was supplied.
     */
    cliMatch?: string;
  };
  customer?: {
    name: string;
    onlineCardNumber: string;
    cif: string;
    segment?: string;
    registrationType?: string;
    cliMatch?: string;
    customerVerification?: string;
    dob?: string;
    assistanceRequired?: string;
  };
}

/**
 * A customer hit from the NSC + account-number lookup (CCF-7). Reuses the
 * shared `/customers/search` contract (CCF-44). When the customer has a real
 * online card on file, that card number drives the existing online-card
 * verification flow; otherwise CCF-7 surfaces "No online card number on file".
 */
interface NscAccountHit {
  CustomerNumber?: string;
  CustomerName?: string;
  OnlineCardNumber?: string;
  HasOnlineCard?: boolean;
  /**
   * #851 CR Change 2 (CCRS-3943): set by the BFF only for a customer holding
   * several online cards. `matched` ⇒ {@link OnlineCards} lists the card(s)
   * that reach the searched account; `no-match` ⇒ no online card reaches the
   * account and ID&V must not proceed (fail closed, Q4); `unverified` ⇒ a card
   * lookup failed, so ID&V proceeds as before this change under the
   * identification API's card (@dylan219, #851).
   */
  ProductSearchMatch?: 'matched' | 'no-match' | 'unverified';
  OnlineCards?: { OnlineCardNumber: string; Last4: string }[];
  /** Client-only: dropdown key (CIF, or CIF + card when expanded per card). */
  CandidateKey?: string;
  /**
   * Client-only: the FULL matched online card number on a per-card candidate
   * (@dylan219, #851: "Show the full card number").
   */
  CardLabel?: string;
}

/**
 * #851 CR Change 2 (CCRS-3943): expand the NSC + account hits into ID&V
 * candidates. A customer holding several online cards becomes one candidate
 * per online card that reaches the searched account (§5.3), each carrying THAT
 * card — never the identification API's card (§5.2). A no-match customer
 * stays a single candidate that is refused on proceed (§5.4); an unverified
 * one (failed lookup, §5.5) is a single candidate under the identification
 * API's card, exactly as before this change (@dylan219).
 */
function expandNscCandidates(hits: NscAccountHit[]): NscAccountHit[] {
  return hits.flatMap((hit): NscAccountHit[] => {
    const cif = hit.CustomerNumber ?? '';
    if (hit.ProductSearchMatch === 'matched' && hit.OnlineCards?.length) {
      return hit.OnlineCards.map((card) => ({
        ...hit,
        OnlineCardNumber: card.OnlineCardNumber,
        HasOnlineCard: true,
        CandidateKey:
          hit.OnlineCards!.length > 1 ? `${cif}:${card.OnlineCardNumber}` : cif,
        CardLabel:
          hit.OnlineCards!.length > 1 ? card.OnlineCardNumber : undefined,
      }));
    }
    return [{ ...hit, CandidateKey: cif }];
  });
}

/** #851: why a flagged NSC + account candidate cannot proceed, if it cannot. */
function blockedCandidateMessage(hit: NscAccountHit): string | undefined {
  if (hit.ProductSearchMatch === 'no-match') {
    return 'No online card gives access to this account, so ID&V cannot continue under an online card.';
  }
  if (hit.ProductSearchMatch === 'matched' && !hit.OnlineCardNumber) {
    return 'Select the online card the caller is using.';
  }
  return undefined;
}

interface PanResult {
  valid: boolean;
  resultCode?: string;
  attemptsRemaining?: number;
  deregister?: boolean;
}

/**
 * Duplicate-login block (SCRN-15 / CCF-13). When the entered credentials are
 * already active in another agent's desktop session, the BFF returns this and
 * the agent must NOT proceed.
 */
interface DuplicateBlock {
  agentName?: string;
  timestamp?: string;
  message?: string;
}

/** Maximum PAN attempts (CCF-1) — mirrors the gateway/mock `MAX_PAN_ATTEMPTS`. */
const MAX_PAN_ATTEMPTS = 3;

/**
 * Maps a customer's online-card type to its registration screen route
 * (CCF-8/CCF-46). Every ID&V → registration entry point (the unregistered-card
 * "Register" button, the post-deregister auto-route, and the de-registered
 * banner's "Go to registration" button) uses this to auto-select the correct
 * registration screen by card type (Open24/Business24/VISA/Mortgage/Broker24),
 * mirroring how each medium-security page is chosen. The type is resolved
 * server-side from the gateway (the gateway-nominated journey, falling back to
 * the customer segment); when it cannot be resolved, callers default to Open24
 * (SCRN-17) — @dylan219 #250: "If we are unsure which registration journey to
 * send them on then go ahead and select Screen 17 for them but ensure they can
 * navigate to different registration screens" (the RegistrationSwitcher process
 * menu on the registration screen, #163). `registrationRouteFor` returns
 * `undefined` for an unresolved type; the caller supplies the Open24 default.
 */
const REGISTRATION_ROUTE_BY_TYPE: Record<string, string> = {
  OPEN24: '/registration/open24',
  BUSINESS24: '/registration/business24',
  VISA: '/registration/visa',
  MORTGAGE: '/registration/mortgage',
  BROKER24: '/registration/broker24',
};

function registrationRouteFor(type: string | undefined): string | undefined {
  return type ? REGISTRATION_ROUTE_BY_TYPE[type] : undefined;
}

/** Required length of an Open24/Business24/Broker24 online card number (CCF-1). */
const ONLINE_CARD_NUMBER_LENGTH = 10;

/**
 * CCF-7 alternative-identification field lengths. The NSC (National Sort Code)
 * is 6 digits and the account number 8 digits; Unibanks validates them as a
 * single combined 14-digit number (NSC + Account Number).
 */
const NSC_LENGTH = 6;
const ACCOUNT_NUMBER_LENGTH = 8;

/**
 * A single PAN-digit box (faithful to the Figma "with boxes" PAN entry):
 * a 60×60 numeric-only single-character input with an inline error.
 */
function PanDigitInput({
  id,
  label,
  value,
  onChange,
  disabled,
  autoAdvanceTo,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  // #395 — id of the element to move focus to once a digit is entered, so the
  // agent doesn't have to tab/click between the three PAN boxes. Omit on the
  // last box (focus stays put once the third digit lands, or moves to Confirm).
  autoAdvanceTo?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col">
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={1}
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => {
          const digit = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
          onChange(digit);
          // Hop focus to the next field only when an actual digit was entered
          // (not on delete/clear), so backspacing stays on the current box.
          if (digit && autoAdvanceTo) {
            document.getElementById(autoAdvanceTo)?.focus();
          }
        }}
        className="w-[60px] h-[60px] text-center border-2 rounded-md font-['Arial',sans-serif] font-bold text-[24px] text-[#1d252c] transition-all focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed hover:border-[#979c9f] border-[#dad7d3]"
      />
    </div>
  );
}

/**
 * SCRN 1 — ID&V ("Customer Verification").
 *
 * Two verification steps, faithful to the customer's Figma design:
 *  1. **Online card number** verification (card-number input + Search + "no card
 *     to hand" checkbox) against the Teller-ID-gated BFF stub
 *     `POST /api/idv/validate-card`.
 *  2. **3-PAN-digit challenge** (CCF-1): once a card validates, the agent must
 *     verify 3 random PAN digits via `POST /api/idv/validate-pan` BEFORE
 *     proceeding to Medium Security. The remaining-attempts counter is shown;
 *     after 3 failures the customer is de-registered (CCF-8) and telephone
 *     banking is blocked. Only a passing PAN flips customer verification to "Y"
 *     and unlocks the security step (CCF-12).
 *
 * Identified-customer details flow into the agent banner via the customer context.
 */
export function IdvScreen(): React.JSX.Element {
  const { customer, setCustomer, updateCustomer } = useCustomer();
  const navigate = useNavigate();
  // #637 — the "Need help?" heading and body are admin-editable scripting text
  // (content catalogue keys `idv.help.title` / `idv.help.body`); each falls back
  // to the compiled-in default.
  const helpTitle = useContent('idv.help.title');
  const helpBody = useContent('idv.help.body');
  // #637 increment 18 — the "Stop and Think" fraud-alert modal guidance copy is
  // admin-editable (title, checks-unavailable message, acknowledgement label);
  // each falls back to the compiled-in default. Text only — the trigger reasons
  // and the acknowledgement flow stay in code.
  const stopAndThinkTitle = useContent('idv.stop_and_think.title');
  const stopAndThinkUnavailable = useContent('idv.stop_and_think.unavailable');
  const stopAndThinkAckLabel = useContent(
    'idv.stop_and_think.acknowledge_label',
  );

  // Teller ID (issue #4 / #24): sourced solely from the agent's user record
  // (session). It is sent automatically on every Teller-ID-gated call and is no
  // longer shown or entered in the UI. When the agent has no assigned Teller ID,
  // `tellerId` is empty and any ID&V attempt is refused with a clear
  // "No teller ID found" message rather than the generic permission error.
  const agentTellerId = useAgentTeller();
  const tellerFromProfile = /^\d{4}$/.test(agentTellerId ?? '');
  const tellerId = tellerFromProfile ? agentTellerId! : '';
  const [cardNumber, setCardNumber] = useState('');
  const [cardNumberError, setCardNumberError] = useState('');
  const [noCardCheckbox, setNoCardCheckbox] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<CardResult | null>(null);

  // CCF-7 alternative identification (NSC + account number). Revealed when the
  // agent ticks "Customer does not have their online card number to hand".
  const [nsc, setNsc] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [nscSearching, setNscSearching] = useState(false);
  const [nscError, setNscError] = useState('');
  // Candidate customers behind the entered NSC + account number. More than one
  // (joint / business / broker accounts) ⇒ the agent picks the correct person
  // from a dropdown (CCF-7). Empty until a search returns ≥ 1 candidate.
  const [nscCandidates, setNscCandidates] = useState<NscAccountHit[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  // Monotonic id for the in-flight NSC/account search. Captured before the
  // awaited `/customers/search` and re-checked after it resolves: if the agent
  // edited the NSC/account (or fired another search) while a request was in
  // flight, `nscSearchSeq.current` will have advanced and the now-STALE response
  // is dropped — it must never resolve a customer (or open PAN verification) for
  // a since-changed query. This closes the wrong-customer ID&V race that editing
  // the inputs *after candidates render* (handleNscChange) does not cover.
  const nscSearchSeq = useRef(0);

  /**
   * Update the NSC or account number and invalidate any prior lookup. Editing
   * either field MUST discard the cached candidates / selection so the agent
   * can never proceed (Continue) against a customer resolved from a stale,
   * since-edited query — that would be a wrong-customer ID&V (CCF-7).
   */
  function handleNscChange(value: string): void {
    // Advance the search sequence so any `/customers/search` still in flight is
    // treated as stale on return (it resolved a now-edited NSC). Also drop the
    // spinner: the stale request's `finally` deliberately won't, so an edit that
    // cancels an in-flight search must re-enable Search here (CCF-7 = unlimited
    // re-entry — the button must never be left stuck on "Searching…").
    nscSearchSeq.current += 1;
    setNscSearching(false);
    setNsc(value.replace(/[^0-9]/g, '').slice(0, NSC_LENGTH));
    setNscCandidates([]);
    setSelectedCandidate('');
    setNscError('');
  }

  function handleAccountNumberChange(value: string): void {
    nscSearchSeq.current += 1;
    setNscSearching(false);
    setAccountNumber(
      value.replace(/[^0-9]/g, '').slice(0, ACCOUNT_NUMBER_LENGTH),
    );
    setNscCandidates([]);
    setSelectedCandidate('');
    setNscError('');
  }

  // CCF-11 "Stop and Think" pre-authentication fraud alert. The acknowledgement
  // must be ticked before the agent may proceed (Search) when a trigger fires.
  const [stopAndThinkAck, setStopAndThinkAck] = useState(false);
  // The positive fraud-check reasons returned by the server-side orchestration
  // route (`GET /idv/stop-and-think`, #460), fetched once the customer is
  // identified. This REPLACES the old single client-derived `callsInLastWeek`
  // signal: the server now runs the full set of checks (recent calls, mobile
  // changed, de-registered, new card) against the customer's own activity in the
  // rolling 48h window. Empty until the check has run (or if it returned no
  // trigger / failed — the advisory is fail-safe and never blocks on error).
  const [stopAndThinkReasonsFromServer, setStopAndThinkReasonsFromServer] =
    useState<string[]>([]);
  // CCF-11 (#498) — the server-side advisory could not be fully evaluated (one
  // or more sub-checks' I/O threw). It carries no positive reasons, but an
  // active fraud advisory cannot be ruled out, so the server-side write gate
  // fails closed on it and the agent must STILL acknowledge before progressing.
  // We surface an acknowledgement affordance for this case too, so the client
  // and server gates can never diverge into a dead-end (nothing to tick).
  const [stopAndThinkIndeterminate, setStopAndThinkIndeterminate] =
    useState(false);
  // CCF-11 (#498) — has the server-side "Stop and Think" advisory finished
  // running for the currently-identified customer? The check is async, so an
  // IDENTIFIED customer's fraud verdict is unknown for a short window after
  // mount/re-entry. During that window we must NOT let the agent progress (the
  // reasons may still arrive and block them) — otherwise the disabled action
  // buttons flicker enabled→disabled and a fast click (or a Playwright poll)
  // can slip through before the verdict lands. Starts false; the advisory
  // effect flips it true once the GET resolves (success OR error — fail-safe),
  // and back to false whenever a new customer is identified.
  const [stopAndThinkChecked, setStopAndThinkChecked] = useState(false);
  // CCF-11 (#535) — the advisory is presented as a BLOCKING MODAL (centred
  // pop-up per the figma), not an inline box. This is the modal's local tickbox
  // state: it only enables the OK button; the acknowledgement is recorded
  // server-side when OK is clicked (`acknowledgeStopAndThink`). Kept separate
  // from `stopAndThinkAck` so an unticked→ticked box never calls the server
  // before the agent confirms with OK.
  const [stopAndThinkModalTicked, setStopAndThinkModalTicked] = useState(false);
  // True while the acknowledge POST is in flight (OK stays disabled so the
  // agent can't double-fire it).
  const [stopAndThinkAckPending, setStopAndThinkAckPending] = useState(false);
  // CCF-11 (#588) — the online-IVR fast-path (Verification=PAN) skips the PAN
  // challenge and routes straight to Medium Security. It used to `navigate`
  // immediately, which unmounted THIS screen before the Stop-and-Think advisory
  // effect could run — so the fraud advisory (which is only rendered here) was
  // never shown on online-IVR calls. We now DEFER that route: this flag is set
  // instead of navigating, and the effect below fires the route only once the
  // advisory has been checked and (if it tripped) acknowledged on this screen.
  const [pendingIvrMediumNav, setPendingIvrMediumNav] = useState(false);

  // Duplicate-login block (SCRN-15 / CCF-13). Non-null ⇒ the entered credentials
  // are in use in another active session and the agent cannot proceed.
  const [duplicateBlock, setDuplicateBlock] = useState<DuplicateBlock | null>(
    null,
  );

  // PAN-challenge state (CCF-1). The gateway picks WHICH 3 PAN positions to
  // challenge per call (cardno/validate's RandomDigits) — NOT always 1/2/3 — so
  // we render the positions it returned and ask the agent for those digits (#66).
  const [showPanPanel, setShowPanPanel] = useState(false);
  const [panPositions, setPanPositions] = useState<PanPositions>(
    DEFAULT_PAN_POSITIONS,
  );
  const [panDigit1, setPanDigit1] = useState('');
  const [panDigit2, setPanDigit2] = useState('');
  const [panDigit3, setPanDigit3] = useState('');
  const [panAttemptsRemaining, setPanAttemptsRemaining] =
    useState(MAX_PAN_ATTEMPTS);
  const [validatingPan, setValidatingPan] = useState(false);
  const [panError, setPanError] = useState('');

  // CCF-8 / CCF-46 — "Customer does not know their PAN". When ticked, a
  // Deregister button is revealed; clicking it deregisters the customer's online
  // card (telephone + internet banking blocked) and routes the agent to the
  // relevant registration screen by online-card type.
  const [panUnknown, setPanUnknown] = useState(false);
  const [deregistering, setDeregistering] = useState(false);
  const [deregisterError, setDeregisterError] = useState('');
  // #317 — the de-registered banner's wording must reflect WHY the customer
  // was de-registered: automatically after 3 failed PAN attempts, or because
  // the agent ticked "Customer does not know their PAN" (no PAN was ever
  // entered on that path, so the two cases need distinct copy).
  const [deregisterReason, setDeregisterReason] = useState<
    'panAttempts' | 'panUnknown' | null
  >(null);

  const panVerified = customer.securityLevel === 'pan';
  const deregistered = customer.deregistered === true;
  const customerNumber = customer.cif ?? '';

  // CCF-11: pre-authentication fraud-alert reasons. These now come from the
  // server-side "Stop and Think" orchestration route (#460), which runs the full
  // configurable check set (multiple recent calls, mobile changed, de-registered,
  // new online/Business24 card created) against the identified customer's own
  // activity in the rolling 48-weekday-hour window — replacing the earlier
  // single client-derived `callsInLastWeek` signal. Only positive triggers are
  // returned; the list is empty until the customer is identified (and the check
  // fires) or if it returned no trigger.
  const stopAndThinkReasons = stopAndThinkReasonsFromServer;
  const stopAndThinkTriggered = stopAndThinkReasons.length > 0;
  // An ACTIVE advisory exists when there are positive reasons OR the verdict was
  // indeterminate (checks temporarily unavailable) — either way the agent must
  // acknowledge before progression (#498). This is the client-side mirror of the
  // server's `hasActiveAdvisory`.
  const stopAndThinkActive = stopAndThinkTriggered || stopAndThinkIndeterminate;
  // The agent is blocked from proceeding until the fraud check has run AND (if
  // it tripped) the alert is acknowledged (CCF-11, #498). Made MONOTONIC on the
  // identified-customer path: while the async advisory is still in flight for an
  // identified customer (`cif` set, verdict not yet in), we block — so the
  // action buttons never flicker enabled→disabled and a click can't race ahead
  // of the verdict. No customer identified ⇒ never blocking (the initial card
  // Search must stay enabled). Fail-safe: the advisory effect always flips
  // `stopAndThinkChecked` true (even on error), so this can never latch on.
  const stopAndThinkBlocking =
    !!customer.cif &&
    (!stopAndThinkChecked || (stopAndThinkActive && !stopAndThinkAck));

  // #201 (ONLINE journey) — auto-trigger the card-lookup chain once per IVR
  // card. The IVR captured the Online Card Number (and possibly the PAN); when
  // it is present we run the SAME validate flow as a manual agent Search
  // (@dylan219: auto-trigger, not prefill), forwarding the IVR PAN-skip hint so
  // the BFF can (server-side) skip to Medium Security when `Verification=PAN`.
  // Guarded by a ref so it fires at most once per distinct card, never on a
  // manual/offline entry, and never after a customer is already identified.
  const ivrAutoTriggeredFor = useRef<string>();

  // Issue #72 (@dylan219, 2026-08-21) — auto-trigger the card lookup once for a
  // card number the Customer Search screen seeded (`customer.pendingIdvCardNumber`).
  // A non-privileged role that selected a REGISTERED customer while ONLINE was
  // shown a verification-required warning and clicked "Go to ID&V"; per the
  // ruling we then use the online card number returned in the search to trigger
  // the card-number lookup and present the agent with the PAN option, as per the
  // normal ID&V process. Guarded by a ref so it fires at most once per distinct
  // card. Unlike the #201 IVR path this is NOT bound to a live conversation and
  // carries NO PAN-skip — it is the exact equivalent of the agent typing the card
  // number and pressing Search.
  const searchAutoTriggeredFor = useRef<string>();

  // CCF-11 (#460) — the "Stop and Think" server check runs at most once per
  // identified customer. Guarded by a ref keyed on the CIF (mirrors
  // `ivrAutoTriggeredFor`), so it never re-fires on re-render and never blocks
  // the screen if the request fails (the advisory is fail-safe).
  const stopAndThinkFetchedFor = useRef<string>();
  // #681 (CCF-10) — the clear-session nonce last handled by this screen. App's
  // Clear Session handler bumps `customer.clearSessionNonce` on every clear-down;
  // when the value seen here changes we reset the local identification inputs.
  // Seeded from the current value so a fresh mount (no clear-down since) does NOT
  // wipe inputs the agent may have just typed.
  const lastClearSessionNonce = useRef<number | undefined>(
    customer.clearSessionNonce,
  );
  // #515 — recovery counter: incrementing this causes the advisory effect to
  // re-run (after the ref guard is reset), recovering from a 403
  // stop_and_think_unacknowledged received when the prior GET silently failed.
  const [stopAndThinkRefetchCount, setStopAndThinkRefetchCount] = useState(0);

  function handleCardNumberChange(value: string): void {
    // CCF-1: non-numeric characters are invalid. Reject them inline rather than
    // silently stripping, so the agent sees a clear warning.
    if (value && !/^[0-9]*$/.test(value)) {
      setCardNumberError('Please enter numbers only');
      return;
    }
    // Clear any prior error once the input is numeric again; the 10-digit length
    // rule is enforced on Search (and below) so the agent can keep typing.
    setCardNumberError('');
    setCardNumber(value);
  }

  /**
   * CCF-1 card-number format validation: an online card number must be exactly
   * 10 digits. Returns an inline error message, or `null` when the format is
   * valid. Used to gate Search and to surface the error inline.
   */
  function cardNumberFormatError(value: string): string | null {
    if (!/^[0-9]*$/.test(value)) {
      return 'Please enter numbers only';
    }
    if (value.length !== ONLINE_CARD_NUMBER_LENGTH) {
      return `Online card number must be ${ONLINE_CARD_NUMBER_LENGTH} digits`;
    }
    return null;
  }

  function resetPanState(): void {
    setShowPanPanel(false);
    setPanDigit1('');
    setPanDigit2('');
    setPanDigit3('');
    setPanAttemptsRemaining(MAX_PAN_ATTEMPTS);
    setPanError('');
    // CCF-8/CCF-46 — a fresh search/lookup resets the PAN-unknown affordance.
    setPanUnknown(false);
    setDeregisterError('');
    setDeregisterReason(null);
  }

  /**
   * CCF-8 / CCF-46 — deregister the identified customer's online card number.
   * Reached when the agent ticks "Customer does not know their PAN" and clicks
   * Deregister (BEFORE PAN verification), or automatically after 3 failed PAN
   * attempts. On success the customer's telephone + internet banking access is
   * blocked and the de-registered banner is shown, presenting the agent with the
   * OPTION to go to the relevant registration screen (by identification-derived
   * type) — the screen does NOT auto-navigate there (#266, @supervisor). On a
   * technical failure the agent sees an inline error (Unhappy Path).
   *
   * `reason` (#317) records WHICH path triggered the deregistration —
   * `'panAttempts'` (3 failed PAN entries) or `'panUnknown'` (agent-initiated,
   * customer doesn't know their PAN) — so the de-registered banner can show
   * accurate copy instead of always claiming the PAN was entered incorrectly.
   */
  async function handleDeregister(
    reason: 'panAttempts' | 'panUnknown',
  ): Promise<void> {
    // CCF-11 (#498): de-registration blocks banking + routes to registration —
    // a forward step that must also wait behind the "Stop and Think"
    // acknowledgement. (The automatic 3-failed-attempts call is already gated by
    // handleConfirmPan; this guards the manual "does not know their PAN" path.)
    if (stopAndThinkBlocking) {
      toast.error('Acknowledge the Stop and Think alert before proceeding');
      return;
    }
    if (!customerNumber) {
      toast.error('No identified customer. Search for a card number first.');
      return;
    }
    setDeregisterReason(reason);
    setDeregistering(true);
    setDeregisterError('');
    try {
      const { data, error, response } = await api.POST('/idv/deregister', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      });
      if (response.status === 403) {
        const code = (error as { error?: string } | undefined)?.error;
        if (code === 'stop_and_think_unacknowledged') {
          // #515 — same recovery as validate-pan.
          setStopAndThinkChecked(false);
          stopAndThinkFetchedFor.current = undefined;
          setStopAndThinkRefetchCount((c) => c + 1);
          toast.error('Acknowledge the Stop and Think alert before proceeding');
        } else {
          toast.error('Forbidden: check Teller ID and permissions.');
          setDeregisterError('Forbidden: check Teller ID and permissions.');
        }
        return;
      }
      const body = (data ?? { deregistered: false }) as {
        deregistered: boolean;
        registrationType?: string;
      };
      if (!body.deregistered) {
        // CCF-8 Unhappy Path — technical error on the backend: surface an error
        // and do NOT route away; the agent can retry.
        setDeregisterError(
          'Deregistration could not be completed. Please try again.',
        );
        toast.error('Deregistration failed — please try again');
        return;
      }
      // Telephone + internet banking blocked. Mark the customer de-registered,
      // close the PAN panel, and surface the de-registered banner. Do NOT
      // auto-navigate to registration — the agent decides whether to proceed.
      //
      // #266 (@supervisor): when the card comes back de-registered the agent
      // must be PRESENTED WITH THE OPTION to go to the registration page, not
      // taken there automatically. The de-registered banner rendered below owns
      // that agent-initiated "Go to registration" button; clicking it (and only
      // clicking it) routes to the registration page, where the required lookups
      // run via `customer/identification`. This reverses #170's immediate
      // auto-route (the tester confirmed the de-registered MESSAGE itself is
      // "perfect" and unchanged).
      //
      // The deregister response's `registrationType` is still resolved
      // server-side from the **`customer/identification`** API (CIF Segment) —
      // the authoritative source for the registration journey. PERSIST it onto
      // the customer context so the banner's "Go to registration" button routes
      // by that identification-derived type, not the staler type captured at
      // `cardno/validate` time (a DIFFERENT backend — Dylan #156).
      const registrationType =
        body.registrationType ?? customer.registrationType;
      updateCustomer({
        deregistered: true,
        customerVerification: 'N',
        ...(registrationType ? { registrationType } : {}),
      });
      setShowPanPanel(false);
      toast.success('Customer de-registered — telephone banking blocked');
    } catch {
      setDeregisterError(
        'Deregistration could not be completed. Please try again.',
      );
      toast.error('Request failed.');
    } finally {
      setDeregistering(false);
    }
  }

  /**
   * CCF-1 (#163 points 2-3, #234) — start registration for an UNREGISTERED online
   * card (gateway code 1001). The BFF resolves the customer via
   * customer/identification and returns it as `registrationCustomer`; put that
   * CIF/name/segment into the agent-banner context BEFORE navigating so the
   * registration screen has a customer (otherwise it shows "No customer is in
   * context"), preserving the call-derived enrichment (CLI + calls-in-last-week).
   * Route to the resolved segment's registration page, defaulting to Open24 when
   * the segment is unresolvable (per @dylan219 #163: "If we have no clue as to
   * their segment route them to Open24"). If identification did not resolve a
   * customer, route to Open24 with no context (unchanged fallback). This is NOT
   * the ID&V verification path — the registration screen re-verifies the customer.
   */
  function handleRegister(res: CardResult): void {
    const rc = res.registrationCustomer;
    // Build a FRESH customer context for the registration flow — never spread
    // the prior `customer`. The registration screen re-verifies the customer, so
    // the ONLY fields safe to carry across are call-scoped (non-customer-specific)
    // enrichment from the live interaction: the caller's CLI/ANI, the derived
    // calls-in-last-week count, and the agent's Teller ID (all keyed to the call,
    // not to any identified customer). Everything customer-specific is sourced
    // solely from the newly-resolved `registrationCustomer` and left undefined
    // (cleared) whenever that field is absent — spreading the prior customer here
    // would let their name/dob/segment/onlineCardNumber survive into the banner +
    // sessionStorage, mixing two customers' PII (cross-customer leak). The card is
    // unregistered so nothing is verified yet; the registration flow runs its own
    // security checks + 2FA.
    const callScoped: CustomerContext = {
      ...(customer.cli ? { cli: customer.cli } : {}),
      ...(customer.callsInLastWeek !== undefined
        ? { callsInLastWeek: customer.callsInLastWeek }
        : {}),
      // #768 (CCF-3): populate the banner CLI match on the registration screen.
      // It is sourced ONLY from the newly-resolved `registrationCustomer`
      // (BFF-computed against THAT customer's registered mobile) — never from
      // the prior `customer.cliMatch`, which was computed against a DIFFERENT
      // customer's mobile and would be a cross-customer leak (#234). Absent when
      // no customer resolved, so the banner falls back to "N".
      ...(rc?.cliMatch ? { cliMatch: rc.cliMatch } : {}),
      tellerId,
      customerVerification: 'N',
      securityLevel: 'none',
      deregistered: false,
    };
    if (rc?.cif) {
      // A customer resolved for the unregistered card — seed the fresh context
      // with their identity fields (each still cleared if `rc` omits it).
      setCustomer({
        cif: rc.cif,
        ...(rc.name ? { name: rc.name } : {}),
        ...(rc.onlineCardNumber
          ? { onlineCardNumber: rc.onlineCardNumber }
          : {}),
        ...(rc.segment ? { segment: rc.segment } : {}),
        ...(rc.registrationType
          ? { registrationType: rc.registrationType }
          : {}),
        ...(rc.dob ? { dob: rc.dob } : {}),
        ...callScoped,
      });
    } else {
      // Fallback (identification declined / errored, or none was attempted): route
      // to registration with NO customer in context — but we must still WRITE that
      // empty context. If we simply navigated, any previous customer's CIF/name/
      // DoB/segment would remain in the banner + sessionStorage, so the fresh
      // (unregistered) registration would show — and could act on — the wrong
      // customer's PII. Reset to the call-scoped base only, clearing all
      // customer-specific fields.
      setCustomer({ ...callScoped });
    }
    const route =
      registrationRouteFor(rc?.registrationType) ??
      REGISTRATION_ROUTE_BY_TYPE.OPEN24 ??
      '/registration/open24';
    navigate(route);
  }

  async function handleSearch(): Promise<void> {
    // The Teller ID is sourced from the agent's profile and is mandatory on
    // every gated call. An agent with no assigned Teller ID cannot run ID&V —
    // surface a clear message rather than the generic permission error
    // (issue #24). The server-side Teller-ID gate remains the enforcement point.
    if (!/^\d{4}$/.test(tellerId)) {
      setResult({ found: false, message: 'No teller ID found' });
      toast.error('No teller ID found');
      return;
    }
    if (!cardNumber) {
      toast.error('Please enter an Online Card Number');
      return;
    }
    // CCF-11: when a fraud trigger has fired, the agent must acknowledge the
    // "Stop and Think" alert before proceeding with any process.
    if (stopAndThinkBlocking) {
      toast.error('Acknowledge the Stop and Think alert before proceeding');
      return;
    }
    // CCF-1: enforce the 10-digit numeric format before any lookup. The agent
    // re-enters until a valid format is provided.
    const formatError = cardNumberFormatError(cardNumber);
    if (formatError) {
      setCardNumberError(formatError);
      toast.error(formatError);
      return;
    }

    await validateOnlineCard(cardNumber);
  }

  /**
   * Run the online-card verification flow for `card` (the duplicate-login gate,
   * card validation, customer binding and PAN-challenge open). Shared by the
   * primary online-card path (`handleSearch`) and the CCF-7 NSC + account-number
   * path (`handleNscSearch`), which resolves the customer's online card number
   * behind the NSC + account and then verifies it here — so both routes converge
   * on the same CCF-13 / CCF-1 / CCF-12 logic.
   *
   * `dupCredentials` (CCF-7 NSC/account path only) carries the Sort Code +
   * Account number the agent ENTERED; when present the CCF-13 duplicate-login
   * gate registers those credentials (per the `/idv/duplicate-check` contract)
   * instead of the resolved online card, so account-based identification
   * collides on the fraud service the same way card-based identification does.
   *
   * `isStale` (CCF-7 NSC/account path only) reports whether the originating
   * NSC/account query has since changed (the agent edited the fields, unticked
   * the no-card checkbox, or started another search). It is re-checked after
   * EVERY awaited gateway call here, so a response that returns after the query
   * moved on can never identify the customer or open PAN verification — closing
   * the wrong-customer ID&V race across the whole validate chain (fail closed).
   */
  async function validateOnlineCard(
    card: string,
    dupCredentials?: { sortCode: string; accountNumber: string },
    isStale?: () => boolean,
    ivrVerified?: boolean,
    // CCF-112 (#477) — this lookup originated from the IVR screen-pop
    // auto-identify (#201), independent of whether the IVR also verified the PAN
    // (`ivrVerified`). Drives the "Identified Via IVR" call-history method.
    viaIvr?: boolean,
  ): Promise<void> {
    const stale = (): boolean => (isStale ? isStale() : false);
    setSearching(true);
    setResult(null);
    setDuplicateBlock(null);
    resetPanState();
    toast.message('Searching for card number…');
    try {
      // SCRN-15 / CCF-13: before identifying the customer, check the entered
      // credentials are not already active in another agent's desktop session.
      // Team Leaders/Managers are exempt server-side (this returns blocked:false
      // for them). A normal, non-duplicate card returns blocked:false and the
      // flow continues exactly as before.
      // On the CCF-7 NSC/account path, key the duplicate check by the Sort Code
      // + Account number the agent entered (the credentials a colliding desktop
      // would also present); on the primary path, by the online card number.
      const dup = await api.POST('/idv/duplicate-check', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: dupCredentials
          ? {
              sortCode: dupCredentials.sortCode,
              accountNumber: dupCredentials.accountNumber,
            }
          : { onlineCardNumber: card },
      });
      // The NSC/account query moved on while the duplicate-check was in flight —
      // abandon silently (fail closed); the now-stale customer must not be
      // identified.
      if (stale()) return;
      if (dup.response.status === 403) {
        toast.error('Forbidden: check Teller ID and permissions.');
        setResult({
          found: false,
          message: 'Forbidden: check Teller ID and permissions.',
        });
        return;
      }
      if (dup.data?.blocked) {
        // BLOCKED — surface the warning and stop. The agent cannot proceed with
        // any process (no card lookup, no PAN challenge) while blocked.
        setDuplicateBlock({
          agentName: dup.data.agentName,
          timestamp: dup.data.timestamp,
          message: dup.data.message,
        });
        toast.error('Online card number already in use — do not proceed');
        return;
      }

      // #201 (ONLINE journey) — when requesting the IVR PAN-skip, forward the
      // agent's short-lived Genesys token + the conversation id so the BFF can
      // re-read the `Verification=PAN` signal SERVER-SIDE from the authorised
      // conversation. `ivrVerified` in the body is only a HINT; the BFF never
      // trusts it alone (IDOR-safe — it confirms against Genesys itself, and
      // binds the read to the current-live interaction the request resolves to).
      //
      // The conversation id comes from `customer.ivrConversationId` — the id
      // App.tsx FROZE at mount, bound to the authorised live interaction it
      // enriched. It is deliberately NOT re-read from the live URL here: the
      // screen-pop landing navigates to `/idv`, which DROPS the
      // `?gcInteractionId=…` query string (see App.tsx), so a live
      // `readEmbedContext()` at this point returns no interaction id — the BFF
      // would then never receive the `X-Genesys-Interaction-Id` header and the
      // skip would silently fail closed (the #201 real-call regression reported
      // by @dylan219: no auto-lookup, no route to Medium Security).
      // #343: the Genesys token is NOT forwarded — the BFF reads it from the
      // server-side session when it re-confirms the IVR PAN signal. Only the
      // interaction-id claim is passed so the server binds the skip to the live
      // conversation.
      const conversationId = ivrVerified
        ? customer.ivrConversationId
        : undefined;
      const { data, error, response } = await api.POST('/idv/validate-card', {
        // 4-digit Teller ID is mandatory on every banking call.
        params: {
          header: {
            'X-Teller-Id': tellerId,
            ...(ivrVerified && conversationId
              ? { 'X-Genesys-Interaction-Id': conversationId }
              : {}),
          },
        },
        // Forward the live call's CLI (from the Genesys screen-pop enrichment,
        // if any) so the gateway can compute the CLI-match.
        body: {
          onlineCardNumber: card,
          ...(customer.cli ? { cli: customer.cli } : {}),
          // #201 — request the IVR PAN-skip; the BFF confirms it server-side.
          ...(ivrVerified ? { ivrVerified: true } : {}),
        },
      });

      // The query moved on while validate-card was in flight — fail closed.
      if (stale()) return;
      if (response.status === 403) {
        toast.error('Forbidden: check Teller ID and permissions.');
        setResult({
          found: false,
          message: 'Forbidden: check Teller ID and permissions.',
        });
        return;
      }

      // A non-OK HTTP response is a TRANSPORT/GATEWAY failure, NOT a business
      // "card not found" (issue #156). The Bank gateway returns its own envelope
      // on both a 4xx (`{ error:'gateway_error', gateway:{code,message} }`) and a
      // 5xx (`{ error:'internal_error' }`) — e.g. `cardno/validate` going down with
      // gateway Error 1510 "Routing to Microservice has failed" returns a 500. The
      // SAME online card resolves fine on `customer/identification` (Customer
      // Search), so presenting this as "Card not found" wrongly told the agent the
      // card was invalid and sent them down a dead end. Surface a recoverable
      // error beside the Search action (the gateway code, when present) so the
      // agent knows the lookup TEMPORARILY failed and can retry — not that the
      // card is bad. `response.ok` covers the verified-data (2xx) path below.
      if (!response.ok) {
        const gw = (error ?? {}) as {
          gateway?: { code?: string | number; message?: string };
        };
        const code =
          gw.gateway?.code === undefined ? '' : String(gw.gateway.code).trim();
        const gwMessage = gw.gateway?.message?.trim();
        setResult({
          found: false,
          message:
            gwMessage ||
            'Could not verify the card number right now — the Bank service did not respond. Please try again.',
          ...(code ? { resultCode: code } : {}),
        });
        toast.error('Could not verify the card number — please try again');
        return;
      }

      if (data?.blocked) {
        // validate-card is the AUTHORITATIVE simultaneous-login gate. The
        // pre-check above usually catches a block first, but honour a block
        // returned here too — it covers the race where another agent claims the
        // card between the pre-check and validate-card (CCF-13). No
        // identification occurred server-side; surface the same warning + stop.
        setDuplicateBlock({
          agentName: data.agentName,
          timestamp: data.timestamp,
          message: data.message,
        });
        // CCF-13 (#413): also raise the session-wide freeze overlay — the block
        // is enforced server-side on every route (423), so the whole desktop is
        // frozen, not just this screen. Seeds the global store from the block
        // fields validate-card returned.
        setDuplicateSessionBlock({
          agents: data.agentName
            ? data.agentName.split(',').map((s) => s.trim())
            : [],
          count: 2,
          attemptedAt: data.timestamp ?? new Date().toISOString(),
          message: data.message ?? '',
        });
        toast.error('Online card number already in use — do not proceed');
        return;
      }

      const body = (data ?? {
        found: false,
        message: 'Card not found',
      }) as CardResult;
      setResult(body);

      if (body.found && body.customer) {
        // CCF-112 (#477) — record HOW the caller was identified on this ID&V
        // screen. The three ID&V entry paths converge here at the card-lookup
        // success point, so the method is derived from which path reached it:
        //   - IVR screen-pop auto-identify (#201, `ivrVerified`) → Identified Via IVR
        //   - CCF-7 Sort Code + account (`dupCredentials`) → Account Number/Sort
        //   - manual online-card Search (neither) → O24 Number
        // Fire-and-forget, Teller-ID gated, bound to the identified CIF — a
        // Call-History write failure must NEVER break identification (mirrors
        // the Customer Search + security-event pattern).
        const identificationMethod = viaIvr
          ? 'Identified Via IVR'
          : dupCredentials
            ? 'Agent Identification - Account Number/Sort'
            : 'Agent Identification - O24 Number';
        void api
          .POST('/calls/identification-event', {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: {
              customerNumber: body.customer.cif,
              method: identificationMethod,
            },
          })
          .catch(() => {
            /* best-effort record write — never block identification */
          });

        // #201 (ONLINE journey) — the BFF confirmed SERVER-SIDE that the IVR
        // already verified the customer's PAN (`Verification=PAN`). The server
        // has raised the interaction to `pan`, so skip the PAN challenge
        // entirely and route to Medium Security (doing all the usual lookups).
        // We trust `body.ivrVerified` ONLY because the BFF re-confirmed the
        // signal against the authorised Genesys conversation (it is never set
        // from a client claim alone).
        const ivrPanVerified = body.ivrVerified === true;

        // MERGE the identified customer over the existing context so the
        // pre-auth Genesys enrichment (the call CLI + "calls in last week",
        // CCF-2/CCF-3) is preserved alongside the now-known card details.
        // Remember the Teller ID for the rest of the interaction so subsequent
        // Teller-ID-gated calls (PAN, Medium Security, Dashboard) reuse it.
        // Issue #669 (@dylan219, 2026-09-01): a NEW customer identification
        // drops the PREVIOUS customer's app-shell J&N navigation lock — this
        // same begin-ID&V clears the server-side `requireNoJnHoldBlock`, and
        // the client lock is keyed to the OLD CIF, so it must never survive
        // into a new identification. A fresh J&N verdict for the NEW customer
        // re-raises the lock from its own hold check / dashboard read.
        setJnHoldBlock(null);
        // Customer verification starts at "N" (CCF-12) unless the IVR already
        // verified the PAN (#201) — only the 3-digit challenge (or the IVR)
        // flips it to "Y".
        setCustomer({
          ...customer,
          ...body.customer,
          customerVerification:
            body.customer.customerVerification ?? (ivrPanVerified ? 'Y' : 'N'),
          tellerId,
          // #201: IVR-verified ⇒ ID&V is already complete at `pan` level.
          securityLevel: ivrPanVerified ? 'pan' : 'none',
          deregistered: false,
          // Clear the one-shot IVR card so a later manual re-search / clear
          // session cannot re-trigger the auto-flow for this interaction.
          ivrOnlineCardNumber: undefined,
          ivrPanVerified: undefined,
          // CCF-43 (issue #67): record the identification journey so the
          // Dashboard can run the Customer Authentication Hold Check for the
          // ONLINE journey only. `dupCredentials` is present only on the CCF-7
          // Sort Code + account-number (offline) path, where the hold check is
          // intentionally NOT run.
          idvJourney: dupCredentials ? 'nsc-account' : 'online-card',
          // CCF-43 (#574): a NEW customer identification must reset the hold
          // acknowledgement — the `...customer` spread above would carry
          // `authHoldAcknowledged:true` from the PREVIOUS customer forward,
          // silently suppressing the hold pop-up for a different customer whose
          // hold has not been acknowledged on this call.
          authHoldAcknowledged: undefined,
          // Issue #669: the J&N "acknowledged" client flag was REMOVED
          // (@dylan219, 2026-09-01 — the warning has no acknowledge at all);
          // the per-customer navigation lock is cleared in the store right
          // before this identification lands (see `setJnHoldBlock(null)` above).
          // CCF-11 (#588): a NEW customer identification must ALSO reset the
          // Stop-and-Think acknowledgement/reasons. The `...customer` spread
          // above carries a PRIOR customer's `stopAndThinkAcknowledged:true`
          // (and reasons) forward, which showed a stale "Stop and Think
          // acknowledged" banner and suppressed the fresh advisory for a
          // DIFFERENT customer — the exact symptom reported on the online-IVR
          // fast-path (no fresh pop-up, banner already "acknowledged").
          stopAndThinkAcknowledged: undefined,
          stopAndThinkReasons: undefined,
        });

        // CCF-43 (issue #67): the Customer Authentication Hold Check no longer
        // runs here at ID&V. It now runs on the Dashboard (after authentication)
        // and for the ONLINE journey only — see DashboardScreen.

        if (ivrPanVerified) {
          // #201: PAN already verified in the IVR — do NOT ask again. Skip
          // straight to Medium Security (CCF-1 step 9 / CCF-12), which runs all
          // the usual lookups. Mirrors the auto-route after a manual PAN pass.
          //
          // CCF-11 (#588): do NOT navigate here. An immediate navigate unmounts
          // this screen before the Stop-and-Think advisory effect (keyed on the
          // now-set `customer.cif`) can run, so the fraud advisory — rendered
          // ONLY on this screen — was never shown on online-IVR calls. Flag the
          // pending route instead; the effect below runs it once the advisory
          // has been checked and (if it tripped) acknowledged. With no advisory
          // this is effectively immediate, preserving the fast path.
          setPendingIvrMediumNav(true);
          return;
        }

        // CCF-1: the gateway tells us WHICH PAN positions to challenge
        // (RandomDigits). Use them so the agent asks for the correct digits;
        // fall back to 1/2/3 only when the gateway did not return them (#66).
        const p = data?.panPositions;
        setPanPositions(
          p &&
            typeof p.Position1 === 'number' &&
            typeof p.Position2 === 'number' &&
            typeof p.Position3 === 'number'
            ? {
                Position1: p.Position1,
                Position2: p.Position2,
                Position3: p.Position3,
              }
            : DEFAULT_PAN_POSITIONS,
        );
        // #652: seed "PAN attempts remaining" from the card-search API when it
        // reports a value (the customer may already have burned attempts in a
        // prior session, so a fresh hardcoded 3 would be wrong). resetPanState()
        // has already defaulted it to MAX_PAN_ATTEMPTS, so only override when the
        // API actually carried a count.
        if (typeof data?.attemptsRemaining === 'number') {
          setPanAttemptsRemaining(data.attemptsRemaining);
        }
        // CCF-1: open the PAN challenge — the agent cannot proceed to Medium
        // Security until 3 PAN digits are verified.
        setShowPanPanel(true);
        toast.success('Customer found — verify 3 PAN digits');
      } else {
        // Card not found. Reset to the call-scoped enrichment (CLI + calls in
        // last week) that is valid for the whole call, clearing any prior
        // customer's identity.
        //
        // Issue #679 — when the card is UNREGISTERED / de-registered (code 1001
        // / 1002) the BFF still resolves the customer via customer/identification
        // and returns them as `registrationCustomer`. Previously the agent only
        // saw those details AFTER clicking "Register" (handleRegister seeds the
        // banner from `registrationCustomer` before routing). @philipc39
        // requested the details be shown on the Customer Verification screen
        // itself: seed the banner with the resolved identity (Name / CIF /
        // Online card number) here at Search time — WITHOUT any verification
        // (the card is unregistered, nothing is verified; the customer must
        // re-register). This mirrors handleRegister's identity binding but the
        // agent has not yet chosen to proceed, so we stay on this screen with a
        // populated banner instead of navigating.
        const rc = body.registrationCustomer;
        const callScoped: CustomerContext = {
          ...(customer.cli ? { cli: customer.cli } : {}),
          ...(customer.callsInLastWeek !== undefined
            ? { callsInLastWeek: customer.callsInLastWeek }
            : {}),
          // #768 (CCF-3): populate the banner CLI match for the unregistered
          // card seeded at Search time, sourced ONLY from the resolved
          // `registrationCustomer` (BFF-computed against THAT customer's mobile)
          // — never the prior `customer.cliMatch` (cross-customer leak, #234).
          ...(rc?.cliMatch ? { cliMatch: rc.cliMatch } : {}),
          // Issue #689 — `setCustomer` REPLACES the whole context (it does not
          // merge). The clear-session nonce is a call-scoped signal the ID&V
          // screen's reset-inputs effect watches: when it CHANGES the effect
          // clears the identification inputs AND `setResult(null)`. If we drop
          // it here (undefined) after a prior Clear Session bumped it (≥1), the
          // effect sees a change and wipes the just-set `result` — so the inline
          // de-registered warning + Register button vanish and only the toast
          // remains (the ~50%: it only misfires when a nonce was already set).
          // A re-search then sets it undefined→undefined (no change), so the
          // banner survives — exactly the "search again to display it" symptom.
          // Carry the nonce forward (the found path already does, via
          // `...customer`) so the effect does not misfire.
          ...(customer.clearSessionNonce !== undefined
            ? { clearSessionNonce: customer.clearSessionNonce }
            : {}),
          customerVerification: 'N',
          securityLevel: 'none',
          deregistered: false,
        };
        if (rc?.cif) {
          setCustomer({
            cif: rc.cif,
            ...(rc.name ? { name: rc.name } : {}),
            ...(rc.onlineCardNumber
              ? { onlineCardNumber: rc.onlineCardNumber }
              : {}),
            ...(rc.segment ? { segment: rc.segment } : {}),
            ...(rc.registrationType
              ? { registrationType: rc.registrationType }
              : {}),
            ...(rc.dob ? { dob: rc.dob } : {}),
            ...callScoped,
          });
        } else {
          // No customer resolved (a genuine "card not found" / declined lookup):
          // clear the identified details, keeping only the call-scoped
          // enrichment. Preserve the clear-session nonce too (issue #689): this
          // REPLACE must not look like a fresh clear-down to the reset-inputs
          // effect, which would otherwise wipe the "Card not found" result.
          setCustomer({
            ...(customer.cli ? { cli: customer.cli } : {}),
            ...(customer.callsInLastWeek !== undefined
              ? { callsInLastWeek: customer.callsInLastWeek }
              : {}),
            ...(customer.clearSessionNonce !== undefined
              ? { clearSessionNonce: customer.clearSessionNonce }
              : {}),
          });
        }
        toast.error(body.message || 'Card not found');
      }
    } catch {
      setResult({ found: false, message: 'Request failed.' });
      toast.error('Request failed.');
    } finally {
      setSearching(false);
    }
  }

  function nscAccountFormatError(): string | null {
    // No assigned Teller ID ⇒ ID&V cannot run (issue #24); show a clear message.
    if (!/^\d{4}$/.test(tellerId)) return 'No teller ID found';
    if (!/^[0-9]+$/.test(nsc) || nsc.length !== NSC_LENGTH) {
      return `Sort code (NSC) must be ${NSC_LENGTH} digits`;
    }
    if (
      !/^[0-9]+$/.test(accountNumber) ||
      accountNumber.length !== ACCOUNT_NUMBER_LENGTH
    ) {
      return `Account number must be ${ACCOUNT_NUMBER_LENGTH} digits`;
    }
    return null;
  }

  /**
   * Resolve the customer(s) behind an NSC + account number (CCF-7). On a single
   * match the online-card verification runs immediately; on multiple matches
   * (joint / business / broker accounts) the agent first picks the correct
   * person from a dropdown, then proceeds. Reuses the shared, RBAC-gated
   * `/customers/search` route (the `searchCustomers` action is granted to every
   * role) — no new gateway contract is introduced.
   */
  async function handleNscSearch(): Promise<void> {
    const formatError = nscAccountFormatError();
    if (formatError) {
      setNscError(formatError);
      toast.error(formatError);
      return;
    }
    // Snapshot the exact credentials this request is for and claim a sequence
    // id. The response is only honoured if BOTH still hold on return: the
    // sequence is unchanged (no newer search / no edit advanced it) AND the live
    // inputs still equal the snapshot. Otherwise the response is stale and is
    // dropped — it must never resolve a customer for a since-edited query.
    const reqSeq = (nscSearchSeq.current += 1);
    const reqNsc = nsc;
    const reqAccount = accountNumber;
    const isStale = (): boolean =>
      nscSearchSeq.current !== reqSeq ||
      nsc !== reqNsc ||
      accountNumber !== reqAccount;

    setNscSearching(true);
    setNscError('');
    setNscCandidates([]);
    setSelectedCandidate('');
    setResult(null);
    toast.message('Searching by sort code and account number…');
    try {
      const { data, error, response } = await api.POST('/customers/search', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { nsc: reqNsc, accountNumber: reqAccount },
      });
      // The agent edited the NSC/account (or started another search) while this
      // one was in flight — discard the now-stale response entirely.
      if (isStale()) return;
      if (response.status === 403) {
        setNscError('Forbidden: check Teller ID and permissions.');
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      if (error || !data) {
        setNscError(`Search failed (${response.status}).`);
        toast.error('Request failed.');
        return;
      }
      // #851 CR Change 2: a customer holding several online cards is expanded
      // to one candidate per online card that reaches the searched account.
      const hits = expandNscCandidates(
        ((data.Results ?? []) as unknown as NscAccountHit[]).filter(
          (h) => !!h.CustomerNumber,
        ),
      );
      if (hits.length === 0) {
        // CCF-7 Unhappy Path #1: the combined 14-digit number is not recognised.
        setNscError(
          'The sort code and account number were not recognised. Please re-check and try again.',
        );
        toast.error('Sort code and account number not recognised');
        return;
      }
      if (hits.length === 1 && hits[0]) {
        // Re-check the snapshot immediately before proceeding: nothing must have
        // changed between the search returning and opening verification. The
        // same `isStale` snapshot is threaded into the validate chain so a later
        // edit/untick during duplicate-check / validate-card / hold-check also
        // aborts (fail closed) — not just here.
        if (isStale()) return;
        await proceedWithCandidate(
          hits[0],
          { sortCode: reqNsc, accountNumber: reqAccount },
          isStale,
        );
        return;
      }
      // CCF-7: more than one customer is associated with the NSC + account
      // number — the agent selects the correct person from a dropdown.
      setNscCandidates(hits);
      toast.message('Select the correct customer to continue');
    } catch {
      if (isStale()) return;
      setNscError('Request failed.');
      toast.error('Request failed.');
    } finally {
      // Only the request that is still current clears the spinner; a superseded
      // request must not flip `nscSearching` for the request that replaced it.
      if (!isStale()) setNscSearching(false);
    }
  }

  /**
   * Continue ID&V for a customer resolved via NSC + account number (CCF-7). If
   * the customer has an online card on file, verify it through the shared
   * online-card flow (which opens the PAN challenge); if not, surface CCF-7's
   * "No online card number on file".
   *
   * `enteredCredentials` carries the Sort Code + Account number the AGENT
   * actually entered. CCF-13 / the `/idv/duplicate-check` contract key a session
   * by the credentials presented, so on the NSC/account path the duplicate-login
   * gate must register the Sort Code + Account number — NOT just the resolved
   * online card — otherwise two desktops identifying the same customer by
   * account would not collide on the fraud service.
   *
   * `isStale` is forwarded into the validate chain so a since-changed NSC/account
   * query (edit / untick / re-search) aborts identification at every awaited step
   * (fail closed) — the agent can never reach PAN for a stale selection.
   */
  async function proceedWithCandidate(
    hit: NscAccountHit,
    enteredCredentials?: { sortCode: string; accountNumber: string },
    isStale?: () => boolean,
  ): Promise<void> {
    // #851 CR Change 2 (§5.4, Q4): no online card reaches the searched
    // account — fail closed, inline beside the action; the identification
    // API's card is never used in its place. (A failed card lookup, §5.5,
    // proceeds as before this change under the identification card.)
    const blocked = blockedCandidateMessage(hit);
    if (blocked) {
      setNscError(blocked);
      toast.error('Online card for this account not confirmed');
      return;
    }
    if (hit.HasOnlineCard && hit.OnlineCardNumber) {
      await validateOnlineCard(
        hit.OnlineCardNumber,
        enteredCredentials,
        isStale,
      );
      return;
    }
    // CCF-7 Unhappy Path #3: no online card number exists on file.
    setResult({
      found: false,
      message: 'No online card number on file.',
    });
    toast.error('No online card number on file');
  }

  async function handleConfirmPan(): Promise<void> {
    // CCF-11 (#498, @dylan219): the "Stop and Think" alert fires once the
    // customer is identified — i.e. exactly when the PAN challenge is on screen.
    // The agent must not be able to progress *anything* (here: verify the PAN
    // and route to Medium Security) until they tick the acknowledgement.
    if (stopAndThinkBlocking) {
      toast.error('Acknowledge the Stop and Think alert before proceeding');
      return;
    }
    if (!panDigit1 || !panDigit2 || !panDigit3) {
      setPanError('Enter all three PAN digits.');
      return;
    }
    if (!customerNumber) {
      toast.error('No identified customer. Search for a card number first.');
      return;
    }
    setValidatingPan(true);
    setPanError('');
    try {
      const { data, error, response } = await api.POST('/idv/validate-pan', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          digits: { Digit1: panDigit1, Digit2: panDigit2, Digit3: panDigit3 },
          // The positions the gateway challenged for THIS customer (#66) — each
          // entered digit corresponds, in order, to Position1/2/3.
          positions: panPositions,
        },
      });
      if (response.status === 403) {
        // CCF-11 (#498): the BFF fails closed while an active Stop-and-Think
        // advisory is unacknowledged (a manipulated-client backstop for the
        // disabled button). Surface it inline beside the action, not as a bare
        // "forbidden", so the agent knows to tick the acknowledgement.
        const code = (error as { error?: string } | undefined)?.error;
        if (code === 'stop_and_think_unacknowledged') {
          setPanError(
            'Acknowledge the Stop and Think alert before proceeding.',
          );
          // #515 — recovery: the advisory GET silently failed earlier (fail-safe),
          // so the client had no alert to show. Re-block and re-fetch now so the
          // advisory alert appears and the agent can acknowledge it to proceed.
          setStopAndThinkChecked(false);
          stopAndThinkFetchedFor.current = undefined;
          setStopAndThinkRefetchCount((c) => c + 1);
        } else {
          toast.error('Forbidden: check Teller ID and permissions.');
        }
        return;
      }
      const body = (data ?? { valid: false }) as PanResult;

      if (body.valid) {
        // CCF-12: PAN verified ⇒ verification flips to "Y"; ID&V is complete.
        updateCustomer({ securityLevel: 'pan', customerVerification: 'Y' });
        toast.success('PAN verified — customer authenticated');
        // CCF-1 step 9 / CCF-12: once the PAN is verified the agent proceeds to
        // the Medium Security screen. Auto-route there rather than requiring a
        // separate manual "Continue" click — the verification work itself is
        // unchanged (the server-side level is now "pan"; the Medium Security
        // step raises it to "medium"). Client-side navigation convenience only.
        // #592 (CCRS-3280): route a recognised Business24 customer to the
        // Business Medium Security journey (SCRN-05) instead of the personal EDS
        // Medium Security screen — the business flag is the server-verified
        // segment on the customer context, never a client-declared field.
        navigate(mediumSecurityRouteFor(customer));
        return;
      }

      // Failed attempt: surface the remaining-attempts counter.
      if (typeof body.attemptsRemaining === 'number') {
        setPanAttemptsRemaining(body.attemptsRemaining);
      }
      setPanDigit1('');
      setPanDigit2('');
      setPanDigit3('');

      if (body.deregister) {
        // CCF-8/CCF-46: 3rd failed PAN attempt ⇒ the system AUTOMATICALLY
        // deregisters the online card (blocking telephone + internet banking)
        // and moves the agent to the appropriate registration screen. Run the
        // same server-side deregister + fail-closed routing as the manual
        // "Customer does not know their PAN" path, rather than only flagging the
        // banner — so the gateway block actually happens and routing is
        // consistent.
        toast.error('PAN incorrect 3 times — de-registering customer');
        await handleDeregister('panAttempts');
      } else {
        setPanError('The PAN digits do not match. Please try again.');
        toast.error('Incorrect PAN digits');
      }
    } catch {
      toast.error('Request failed.');
    } finally {
      setValidatingPan(false);
    }
  }

  // CCF-11 (#498) — record the acknowledgement SERVER-SIDE before unblocking the
  // client. The disabled Confirm PAN / Deregister buttons are only half the
  // progression block; the BFF (`/idv/validate-pan` + `/idv/deregister`) fails
  // closed until this POST has recorded the tick for the identified customer. So
  // only flip the local `stopAndThinkAck` (which enables those buttons and
  // dismisses the #535 modal) AFTER the server confirms — fail-closed, with no
  // window where a button is enabled before the server knows the alert was
  // acknowledged. Invoked from the modal's OK button (enabled only once the
  // "I have read and understood this message" tickbox is ticked).
  async function acknowledgeStopAndThink(): Promise<void> {
    setStopAndThinkAckPending(true);
    try {
      const { response } = await api.POST('/idv/stop-and-think/acknowledge', {
        params: { header: { 'X-Teller-Id': tellerId } },
      });
      if (!response.ok) {
        toast.error('Could not record the acknowledgement. Please try again.');
        return;
      }
      setStopAndThinkAck(true);
      // CCF-11 (#520): persist the acknowledgement into the customer context so
      // the persistent AgentBanner can show a reminder row on all subsequent
      // screens for the duration of this call.
      updateCustomer({
        stopAndThinkAcknowledged: true,
        stopAndThinkReasons: stopAndThinkReasons,
      });
    } catch {
      toast.error('Could not record the acknowledgement. Please try again.');
    } finally {
      setStopAndThinkAckPending(false);
    }
  }

  // CCF-11 (#460) — once the customer is identified (has a CIF), run the
  // server-side "Stop and Think" fraud-advisory check and store its reasons for
  // the banner. Fired at most once per identified CIF (ref-guarded, like the IVR
  // auto-trigger). The customer + card are resolved server-side from the ID&V
  // session, so no identifiers are sent. Fail-safe: any error leaves the reasons
  // empty and NEVER blocks the screen.
  useEffect(() => {
    const cif = customer.cif;
    if (!cif) return;
    // The Teller ID is mandatory on the gated route; without it the request
    // would 400/403. Wait for it (the guard is re-run when tellerId lands).
    if (!/^\d{4}$/.test(tellerId)) return;
    if (stopAndThinkFetchedFor.current === cif) return;
    stopAndThinkFetchedFor.current = cif;
    // #498 — a NEW customer's fraud verdict is not yet known: re-block the
    // progression buttons until this GET resolves (monotonic gate, above).
    setStopAndThinkChecked(false);
    // The acknowledgement is per-customer (recorded server-side against the
    // identified customer): a new CIF's advisory needs its OWN acknowledgement,
    // so reset the local ack + modal tick and clear any stale reasons while the
    // new verdict is in flight — otherwise a prior customer's ack would suppress
    // the #535 modal for the new customer (a dead-end: nothing to acknowledge).
    setStopAndThinkAck(false);
    setStopAndThinkModalTicked(false);
    setStopAndThinkReasonsFromServer([]);
    setStopAndThinkIndeterminate(false);
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.GET('/idv/stop-and-think', {
          params: { header: { 'X-Teller-Id': tellerId } },
        });
        if (cancelled) return;
        setStopAndThinkReasonsFromServer(data?.reasons ?? []);
        setStopAndThinkIndeterminate(data?.indeterminate ?? false);
      } catch {
        // Fail-safe advisory: on any failure show no alert rather than block.
        if (!cancelled) {
          setStopAndThinkReasonsFromServer([]);
          setStopAndThinkIndeterminate(false);
        }
      } finally {
        // #498 — the verdict is now in (success OR error). Unblock the gate so
        // it can never latch on if the advisory call fails or times out.
        if (!cancelled) setStopAndThinkChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customer.cif, tellerId, stopAndThinkRefetchCount]);

  // CCF-11 (#535) — the advisory presents as a BLOCKING MODAL (centred pop-up
  // per the figma) while it is active and unacknowledged; OK dismisses it via
  // the server-side acknowledge flow. Focus the dialog when it opens so
  // keyboard / screen-reader agents land on it immediately. There is
  // deliberately NO Escape-key or backdrop-click dismissal — acknowledgement is
  // mandatory (CCF-11 / #498), so the only way past the modal is tick + OK.
  const stopAndThinkModalOpen = stopAndThinkActive && !stopAndThinkAck;
  const stopAndThinkModalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (stopAndThinkModalOpen) stopAndThinkModalRef.current?.focus();
  }, [stopAndThinkModalOpen]);

  // #201 / CCF-11 (#588) — the online-IVR fast-path defers its route to Medium
  // Security (flagged in `validateOnlineCard`) until the Stop-and-Think advisory
  // has been checked and, if it tripped, acknowledged on THIS screen. Until then
  // `stopAndThinkBlocking` is true and the (already-mounted) advisory modal is
  // presented — so the fraud advisory is shown on IVR calls exactly as it is on
  // the manual path, instead of being skipped when an immediate navigate
  // unmounted the screen. The route fires the moment the advisory clears (no
  // advisory ⇒ effectively immediate, preserving the fast path). `navigate` from
  // react-router is stable, so this cannot re-fire spuriously.
  useEffect(() => {
    if (!pendingIvrMediumNav) return;
    if (stopAndThinkBlocking) return;
    setPendingIvrMediumNav(false);
    toast.success('PAN verified in the IVR — proceeding to Medium Security');
    // #592 (CCRS-3280): Business24 customers go to Business Medium Security
    // (SCRN-05); everyone else to the generic/EDS Medium Security (SCRN-03).
    navigate(mediumSecurityRouteFor(customer));
  }, [pendingIvrMediumNav, stopAndThinkBlocking, navigate, customer]);

  // #201 (ONLINE journey) — auto-trigger the card-lookup chain when the IVR
  // captured the Online Card Number. Runs the same validate flow a manual agent
  // Search would (CCF-13 duplicate-check → validate-card → PAN or, when the IVR
  // verified the PAN, skip to Medium Security), fired at most once per distinct
  // IVR card. Only when there is an assigned Teller ID, no stop-and-think block
  // is pending, and no customer is already identified — so it never overrides an
  // in-progress manual entry or an already-verified interaction.
  useEffect(() => {
    const ivrCard = customer.ivrOnlineCardNumber;
    if (!ivrCard) return;
    // #201 — the IVR hints must belong to the LIVE interaction. App.tsx captures
    // the Genesys launch context ONCE at mount and sets `ivrConversationId` ONLY
    // after it successfully enriches THAT authorised, current-live conversation
    // (and clears all IVR hints on a new/failed enrichment). So the presence of
    // `ivrConversationId` alongside the card IS the live-interaction binding — a
    // prior call's hints can never survive into a later call.
    //
    // We must NOT re-read the interaction id from the live URL here: the
    // screen-pop landing navigates to `/idv`, which DROPS the `?gcInteractionId=…`
    // query string (see App.tsx), so `readEmbedContext()` at this point returns
    // no id — which previously made this guard bail out and the auto-lookup never
    // fired at all (the #201 real-call regression reported by @dylan219).
    if (!customer.ivrConversationId) return;
    if (ivrAutoTriggeredFor.current === ivrCard) return;
    if (!/^\d{4}$/.test(tellerId)) return;
    if (stopAndThinkBlocking) return;
    if (customer.cif) return; // a customer is already identified
    ivrAutoTriggeredFor.current = ivrCard;
    // Reflect the IVR card in the input so the agent sees what is being looked
    // up, then run the validate chain with the IVR PAN-skip hint.
    setCardNumber(ivrCard);
    void validateOnlineCard(
      ivrCard,
      undefined,
      undefined,
      customer.ivrPanVerified,
      // CCF-112 (#477): identified via the IVR screen-pop, regardless of PAN.
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customer.ivrOnlineCardNumber,
    customer.ivrPanVerified,
    customer.ivrConversationId,
    customer.cif,
    tellerId,
    stopAndThinkBlocking,
  ]);

  // Issue #72 (@dylan219, 2026-08-21) — auto-trigger the card lookup from a
  // Customer-Search-seeded Online card number. When a non-privileged role selects
  // a REGISTERED customer on an ONLINE interaction and clicks "Go to ID&V", the
  // Customer Search screen navigates here with `customer.pendingIdvCardNumber`
  // (the 10-digit Open24 number reduced from the search result). We drop it into
  // the card field and run the SAME validate chain as a manual agent Search — no
  // IVR PAN-skip — so the agent is presented with the normal PAN option. The
  // seed is one-shot: it is cleared from context after firing so a later manual
  // Clear/re-identify starts blank, and it never re-fires for the same card.
  useEffect(() => {
    const seededCard = customer.pendingIdvCardNumber;
    if (!seededCard) return;
    if (searchAutoTriggeredFor.current === seededCard) return;
    if (!/^\d{4}$/.test(tellerId)) return;
    if (stopAndThinkBlocking) return;
    if (customer.cif) return; // a customer is already identified
    searchAutoTriggeredFor.current = seededCard;
    // Reflect the card in the input so the agent sees what is being looked up,
    // then run the validate chain exactly as a manual Search would. The ref guard
    // (keyed on the card) makes this a one-shot per distinct card for this mount,
    // mirroring the IVR auto-trigger (`ivrAutoTriggeredFor`); the seed itself is
    // left on the context (like `ivrOnlineCardNumber`) — it is cleared with the
    // rest of the identity context on a Clear Session (#681) / new customer.
    setCardNumber(seededCard);
    void validateOnlineCard(seededCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customer.pendingIdvCardNumber,
    customer.cif,
    tellerId,
    stopAndThinkBlocking,
  ]);

  // #681 (CCF-10) — clear the ID&V identification INPUT fields on Clear Session.
  // Clearing the customer/verification context (App.tsx) does not touch these
  // fields — the Open24 online-card number and the alternative Sort Code + account
  // number are this screen's own local state — so before this a Clear Session left
  // whatever the agent had typed sitting in the inputs. App bumps
  // `customer.clearSessionNonce` on every clear-down (online + offline); on each
  // change we blank those inputs (plus the "no card to hand" toggle, the surfaced
  // lookup result, any candidate list, and the inline errors) so the agent
  // re-identifies from a genuinely blank slate. This deliberately does NOT fire on
  // a card-not-found (which clears the customer but leaves the fields so the agent
  // can correct and retry) — only an explicit Clear Session bumps the nonce.
  useEffect(() => {
    const nonce = customer.clearSessionNonce;
    if (nonce === lastClearSessionNonce.current) return;
    lastClearSessionNonce.current = nonce;
    setCardNumber('');
    setCardNumberError('');
    setNoCardCheckbox(false);
    setNsc('');
    setAccountNumber('');
    setNscCandidates([]);
    setSelectedCandidate('');
    setNscError('');
    setResult(null);
    resetPanState(); // #681: also collapse the PAN panel on Clear Session
  }, [customer.clearSessionNonce]);

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="idv-heading"
    >
      <h1
        id="idv-heading"
        className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
      >
        Customer Verification
      </h1>

      <div className="flex gap-12 mb-8 flex-wrap">
        {/* Online card number verification */}
        <InfoBox className="w-[600px] shrink-0">
          <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-6">
            Online card number verification
          </h2>

          <div className="space-y-6">
            {/* The mandatory 4-digit Teller ID is sourced from the agent's
                profile and sent automatically on every gated call
                (issue #4 / #24); it is no longer shown or entered here. */}

            <div className="flex flex-col gap-2">
              <label
                htmlFor="online-card-number"
                className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
              >
                Online card number
              </label>
              <input
                id="online-card-number"
                inputMode="numeric"
                value={cardNumber}
                onChange={(e) => handleCardNumberChange(e.target.value)}
                placeholder="Enter online card number"
                disabled={noCardCheckbox}
                aria-invalid={!!cardNumberError}
                aria-describedby={
                  cardNumberError ? 'online-card-number-error' : undefined
                }
                style={{ maxWidth: '350px' }}
                className={`px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed hover:border-[#979c9f] ${
                  cardNumberError
                    ? 'border-[#dc2626] focus:border-[#dc2626] focus:ring-[#dc2626]/20'
                    : 'border-[#dad7d3] focus:border-[#005fcc] focus:ring-[#005fcc]/20'
                }`}
              />
              {cardNumberError && (
                <p
                  id="online-card-number-error"
                  role="alert"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                >
                  {cardNumberError}
                </p>
              )}
            </div>

            <div className="flex gap-4">
              <Button
                type="button"
                onClick={() => void handleSearch()}
                disabled={
                  noCardCheckbox ||
                  cardNumberError !== '' ||
                  searching ||
                  stopAndThinkBlocking
                }
                aria-label="Search for online card number"
                className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
              >
                {searching ? 'Searching…' : 'Search'}
              </Button>
            </div>

            {result && (
              <div
                role="status"
                aria-live="polite"
                className={`p-4 border flex items-start gap-3 ${
                  result.found
                    ? 'bg-[#d4edda] border-[#28a745]'
                    : 'bg-white border-[#dc2626]'
                }`}
              >
                {!result.found && (
                  <img
                    src={errorIcon}
                    alt=""
                    aria-hidden="true"
                    className="w-6 h-6 shrink-0"
                  />
                )}
                <div className="flex-1">
                  <p className="font-['Arial',sans-serif] text-[14px] text-[#1D252C] m-0 leading-[1.5]">
                    {result.found && result.customer ? (
                      <>
                        <span className="font-bold">
                          {result.customer.name}
                        </span>{' '}
                        — CIF {result.customer.cif}. {result.message}
                      </>
                    ) : (
                      result.message
                    )}
                  </p>
                  {/* CCF-1 / issue #94 — surface the gateway error code beside
                      the message on an unsuccessful lookup (e.g. a cancelled card
                      9103, or any customer/identification decline). The lookup is
                      a non-blocking WARN: the agent can edit the card number and
                      Search again (the form is never disabled by this). */}
                  {!result.found && result.resultCode && (
                    <p className="font-['Arial',sans-serif] text-[13px] text-[#1D252C] m-0 mt-1 leading-[1.5]">
                      Error code: {result.resultCode}
                    </p>
                  )}
                  {/* CCF-1 (#163, @dylan219; #234) — when the gateway reports the
                      card is unregistered (code 1001) OR already de-registered by
                      an earlier 3-failed-PAN episode (code 1002, #292/#294) the
                      BFF sets `canRegister`. Offer a Register affordance inline
                      beside the warning that starts registration. The BFF also
                      resolves the customer via
                      customer/identification (points 2-3): when it carries a
                      `registrationCustomer`, we put the CIF/name/segment into the
                      agent-banner context here BEFORE routing, so the registration
                      screen has a customer (otherwise it shows "No customer is in
                      context" — #234), and route by the resolved segment (defaulting
                      to Open24 when unresolvable). If no customer resolved we still
                      route to Open24 with no context (unchanged fallback). No other
                      failure code gets this button. */}
                  {!result.found && result.canRegister && (
                    <div className="mt-3 flex flex-col items-start gap-1">
                      <Button
                        type="button"
                        onClick={() => handleRegister(result)}
                        aria-label="Register online card"
                        className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                      >
                        Register
                      </Button>
                      <p className="font-['Arial',sans-serif] text-[13px] text-[#1D252C] m-0 leading-[1.5]">
                        {result.registrationCustomer?.cif
                          ? 'Opens the registration page for the customer’s segment (Open24 by default). Use “Change registration type” to switch.'
                          : 'Opens Open24 registration. Use “Change registration type” on that page to switch to Business24, VISA, Mortgage or Broker24.'}
                      </p>
                    </div>
                  )}
                  {/* On a successful PAN the screen auto-routes to Medium
                      Security (CCF-1 step 9 / CCF-12) — no manual Continue
                      click is required. */}
                </div>
              </div>
            )}

            {/* Simultaneous-login block (SCRN-15 / CCF-13). When the entered
                credentials are already active in another desktop session, BOTH
                agents are blocked: the warning names the other agent + the
                attempt time, and the agent cannot proceed with any process. */}
            {duplicateBlock && (
              <div
                role="alert"
                aria-live="assertive"
                className="p-4 border border-[#dc2626] bg-white flex items-start gap-3"
              >
                <img
                  src={errorIcon}
                  alt=""
                  aria-hidden="true"
                  className="w-6 h-6 mt-1 shrink-0"
                />
                <div className="flex-1 space-y-2">
                  <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0">
                    Number already in use
                  </h3>
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.5]">
                    {duplicateBlock.message}
                  </p>
                  {duplicateBlock.timestamp && (
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.5]">
                      Attempt time: {duplicateBlock.timestamp}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 border-t-2 border-[#e5e7eb] flex items-center gap-3">
              <Checkbox
                id="no-card-checkbox"
                checked={noCardCheckbox}
                onCheckedChange={(v) => {
                  const checked = v === true;
                  setNoCardCheckbox(checked);
                  // Reset the alternative-identification panel when the agent
                  // toggles back to the online-card path. Advancing the search
                  // sequence + clearing the spinner ensures any in-flight
                  // `/customers/search` is treated as stale and can NEVER call
                  // proceedWithCandidate against this since-hidden flow.
                  if (!checked) {
                    nscSearchSeq.current += 1;
                    setNsc('');
                    setAccountNumber('');
                    setNscError('');
                    setNscCandidates([]);
                    setSelectedCandidate('');
                    setNscSearching(false);
                  }
                }}
                disabled={cardNumberError !== ''}
              />
              <label
                htmlFor="no-card-checkbox"
                className={`text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none ${
                  cardNumberError !== ''
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer'
                }`}
              >
                Customer does not have their online card number to hand
              </label>
            </div>

            {/* CCF-7 — alternative identification by NSC (sort code) + account
                number. Revealed once the agent ticks "Customer does not have
                their online card number to hand". The combined NSC + account
                number resolves the customer's online card number, which then
                drives the existing online-card + PAN verification flow above. */}
            {noCardCheckbox && (
              <div className="pt-4 border-t-2 border-[#e5e7eb] space-y-6">
                <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                  Identify by sort code &amp; account number
                </h3>
                <div className="flex gap-4 flex-wrap">
                  <div className="flex flex-col gap-2 w-[160px]">
                    <label
                      htmlFor="idv-nsc"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                    >
                      Sort code (NSC)
                    </label>
                    <input
                      id="idv-nsc"
                      inputMode="numeric"
                      maxLength={NSC_LENGTH}
                      value={nsc}
                      onChange={(e) => handleNscChange(e.target.value)}
                      placeholder="000000"
                      className="px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 hover:border-[#979c9f] border-[#dad7d3] focus:border-[#005fcc] focus:ring-[#005fcc]/20"
                    />
                  </div>
                  <div className="flex flex-col gap-2 w-[220px]">
                    <label
                      htmlFor="idv-account-number"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                    >
                      Account number
                    </label>
                    <input
                      id="idv-account-number"
                      inputMode="numeric"
                      maxLength={ACCOUNT_NUMBER_LENGTH}
                      value={accountNumber}
                      onChange={(e) =>
                        handleAccountNumberChange(e.target.value)
                      }
                      placeholder="00000000"
                      className="px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 hover:border-[#979c9f] border-[#dad7d3] focus:border-[#005fcc] focus:ring-[#005fcc]/20"
                    />
                  </div>
                </div>

                {/* CCF-7 — multiple customers behind the NSC + account number
                    (joint / business / broker): pick the correct person. */}
                {nscCandidates.length > 1 && (
                  <div className="flex flex-col gap-2 w-[360px]">
                    <label
                      htmlFor="idv-candidate"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                    >
                      Select the correct customer
                    </label>
                    <select
                      id="idv-candidate"
                      value={selectedCandidate}
                      onChange={(e) => setSelectedCandidate(e.target.value)}
                      className="px-4 py-3 border-2 border-[#dad7d3] bg-white rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] cursor-pointer focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20"
                    >
                      <option value="">Select a customer…</option>
                      {nscCandidates.map((hit) => (
                        <option
                          key={hit.CandidateKey ?? hit.CustomerNumber}
                          value={hit.CandidateKey ?? hit.CustomerNumber}
                        >
                          {hit.CustomerName} — CIF {hit.CustomerNumber}
                          {hit.CardLabel
                            ? ` — online card ${hit.CardLabel}`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => {
                      if (nscCandidates.length > 1) {
                        const hit = nscCandidates.find(
                          (h) =>
                            (h.CandidateKey ?? h.CustomerNumber) ===
                            selectedCandidate,
                        );
                        if (!hit) {
                          toast.error('Select a customer to continue');
                          return;
                        }
                        // Key the CCF-13 duplicate-check by the entered Sort
                        // Code + Account number on this path too — NOT the
                        // resolved online card. Editing either field clears the
                        // candidate dropdown (handleNsc/AccountNumberChange), so
                        // the live nsc/accountNumber here are guaranteed to be
                        // the credentials that produced these candidates.
                        // Snapshot the sequence + credentials so that editing /
                        // unticking DURING the validate chain (duplicate-check →
                        // validate-card → hold-check) aborts it (fail closed) —
                        // the agent must never reach PAN for a since-changed
                        // selection.
                        // Any edit or untick advances nscSearchSeq (and clears
                        // the candidates), so a change in the sequence is the
                        // authoritative "this selection is stale" signal.
                        const seq = nscSearchSeq.current;
                        void proceedWithCandidate(
                          hit,
                          { sortCode: nsc, accountNumber },
                          () => nscSearchSeq.current !== seq,
                        );
                        return;
                      }
                      void handleNscSearch();
                    }}
                    disabled={nscSearching || searching || stopAndThinkBlocking}
                    aria-label="Search by sort code and account number"
                    className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                  >
                    {nscSearching
                      ? 'Searching…'
                      : nscCandidates.length > 1
                        ? 'Continue'
                        : 'Search'}
                  </Button>
                </div>

                {nscError && (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                  >
                    {nscError}
                  </p>
                )}
              </div>
            )}
          </div>
        </InfoBox>

        {/* PAN verification (CCF-1) — shown once a card has validated. */}
        {showPanPanel && !panVerified && (
          <InfoBox className="w-[600px] shrink-0">
            <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-6">
              PAN verification
            </h2>

            <div className="space-y-6">
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                Ask the customer for the following digits of their PAN.
              </p>

              <div className="flex items-end gap-8">
                <PanDigitInput
                  id="pan-digit-1"
                  label={`${ordinal(panPositions.Position1)} digit`}
                  value={panDigit1}
                  onChange={setPanDigit1}
                  disabled={validatingPan}
                  autoAdvanceTo="pan-digit-2"
                />
                <PanDigitInput
                  id="pan-digit-2"
                  label={`${ordinal(panPositions.Position2)} digit`}
                  value={panDigit2}
                  onChange={setPanDigit2}
                  disabled={validatingPan}
                  autoAdvanceTo="pan-digit-3"
                />
                <PanDigitInput
                  id="pan-digit-3"
                  label={`${ordinal(panPositions.Position3)} digit`}
                  value={panDigit3}
                  onChange={setPanDigit3}
                  disabled={validatingPan}
                  autoAdvanceTo="confirm-pan-button"
                />
                <Button
                  id="confirm-pan-button"
                  type="button"
                  onClick={() => void handleConfirmPan()}
                  disabled={
                    validatingPan ||
                    panAttemptsRemaining === 0 ||
                    stopAndThinkBlocking
                  }
                  aria-label="Confirm PAN digits"
                  className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                >
                  {validatingPan ? 'Confirming…' : 'Confirm PAN'}
                </Button>
              </div>

              {panError && (
                <p
                  role="alert"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                >
                  {panError}
                </p>
              )}

              {/* Remaining-attempts counter (CCF-1 — shown at all times). */}
              <div
                role="status"
                aria-live="polite"
                className="p-4 border bg-[#F8EEE3] border-[#979C9F] w-fit"
              >
                <p className="font-['Arial',sans-serif] text-[14px] text-[#1D252C] m-0 leading-[1.5]">
                  <span className="font-bold">
                    {`PAN attempts remaining: ${panAttemptsRemaining}`}
                  </span>
                  {panAttemptsRemaining === 1 &&
                    ' — this is the final attempt before de-registration.'}
                </p>
              </div>

              {/* CCF-8 / CCF-46 — "Customer does not know their PAN". Ticking it
                  reveals the Deregister button; deregistering blocks telephone +
                  internet banking and routes to the relevant registration
                  screen by online-card type. */}
              <div className="pt-4 border-t-2 border-[#e5e7eb] flex items-center gap-3">
                <Checkbox
                  id="pan-unknown-checkbox"
                  checked={panUnknown}
                  onCheckedChange={(v) => {
                    setPanUnknown(v === true);
                    setDeregisterError('');
                  }}
                  disabled={deregistering}
                />
                <label
                  htmlFor="pan-unknown-checkbox"
                  className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                >
                  Customer does not know their PAN
                </label>
              </div>

              {panUnknown && (
                <div className="space-y-4">
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 leading-[1.5]">
                    Deregistering will block the customer&rsquo;s telephone and
                    internet banking access. You will then be brought to the
                    relevant registration screen.
                  </p>
                  <Button
                    type="button"
                    onClick={() => void handleDeregister('panUnknown')}
                    disabled={deregistering || stopAndThinkBlocking}
                    aria-label="Deregister customer"
                    className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                  >
                    {deregistering ? 'Deregistering…' : 'Deregister'}
                  </Button>
                  {deregisterError && (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    >
                      {deregisterError}
                    </p>
                  )}
                </div>
              )}
            </div>
          </InfoBox>
        )}

        {/* De-registered (CCF-8) — telephone banking blocked. */}
        {deregistered && (
          <InfoBox className="w-[600px] shrink-0 border-l-4 border-l-[#dc2626]">
            <div className="flex gap-3">
              <img
                src={errorIcon}
                alt=""
                aria-hidden="true"
                className="w-6 h-6 mt-1 shrink-0"
              />
              <div className="space-y-4">
                <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0">
                  Customer de-registered
                </h2>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 leading-[1.5]">
                  {deregisterReason === 'panUnknown' ? (
                    <>
                      The customer does not know their PAN. The customer&rsquo;s
                      online card number has been de-registered and telephone
                      banking access is now blocked. Advise the customer they
                      must re-register before they can be verified again.
                    </>
                  ) : deregisterReason === 'panAttempts' ? (
                    <>
                      The PAN was entered incorrectly three times. The
                      customer&rsquo;s online card number has been de-registered
                      and telephone banking access is now blocked. Advise the
                      customer they must re-register before they can be verified
                      again.
                    </>
                  ) : (
                    <>
                      The customer&rsquo;s online card number has been
                      de-registered and telephone banking access is now blocked.
                      Advise the customer they must re-register before they can
                      be verified again.
                    </>
                  )}
                </p>
                <Button
                  type="button"
                  onClick={() => {
                    // #266 (@supervisor) — this is the AGENT-INITIATED option to
                    // go to registration presented on the de-registered result;
                    // the screen never navigates here on its own. Only this click
                    // routes to the registration page, which then runs the
                    // required lookups via `customer/identification` (the customer
                    // — CIF/segment — is still in context so those lookups fire).
                    // Select the registration screen by the customer's
                    // identification-derived type, defaulting to Open24 (SCRN-17)
                    // when it can't be resolved (@dylan219 #250). The agent can
                    // switch pages via the process-menu "Change registration type"
                    // switcher on the registration screen.
                    navigate(
                      registrationRouteFor(customer.registrationType) ??
                        REGISTRATION_ROUTE_BY_TYPE.OPEN24 ??
                        '/registration/open24',
                    );
                  }}
                  aria-label="Go to registration screen"
                  className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                >
                  Go to registration
                </Button>
              </div>
            </div>
          </InfoBox>
        )}

        {/* Quick links */}
        <InfoBox className="w-[350px] shrink-0 self-start">
          <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4 m-0">
            Quick links
          </h2>
          <div className="bg-[#f3f4f6] border-l-4 border-[#fc4c02] pl-4 py-3">
            <a
              href="/customer-search"
              className="text-[16px] font-['Arial',sans-serif] text-[#fc4c02] no-underline hover:underline focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20 focus:underline"
            >
              Customer search
            </a>
          </div>
        </InfoBox>
      </div>

      {/* Need help? */}
      <div className="mt-8 p-4 bg-[#F8EEE3] border border-[#979C9F] w-[600px] flex gap-3">
        <img
          src={infoIcon}
          alt=""
          aria-hidden="true"
          className="w-5 h-5 mt-1 shrink-0"
        />
        <div>
          <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1D252C] mb-2 mt-0">
            {helpTitle}
          </h3>
          <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.5] whitespace-pre-line">
            {helpBody}
          </p>
        </div>
      </div>

      {/* CCF-11 (#535) — "Stop and Think" pre-authentication fraud alert as a
          BLOCKING MODAL, centred in the viewport over the dimmed, inert page
          (the #520 figma), per the tester: "right in front of the agent's
          face", NOT an inline box above the card field. Raised by the
          server-side advisory checks (multiple recent calls, mobile changed,
          de-registered, new card). The full-screen overlay intercepts all
          pointer interaction with the underlying form; Escape does NOT dismiss
          it — the agent must tick "I have read and understood this message"
          and click OK (which records the acknowledgement server-side via the
          existing #498 flow). The affordance ALSO renders on the INDETERMINATE
          path (#498) — when the checks could not be evaluated the server write
          gate fails closed, so the agent must still acknowledge (there must
          never be a dead-end with nothing to tick). The post-acknowledgement
          persistent reminder row is the AgentBanner (#520), unchanged. */}
      {stopAndThinkModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="stop-and-think-title"
          aria-describedby="stop-and-think-reasons"
          data-testid="stop-and-think-modal"
          ref={stopAndThinkModalRef}
          tabIndex={-1}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4 outline-none"
        >
          <div className="max-w-[520px] w-full bg-white border border-[#D23A06] rounded-[4px] px-8 py-6 flex flex-col items-center gap-4 shadow-lg">
            <h2
              id="stop-and-think-title"
              className="text-[16px] font-['Arial',sans-serif] font-bold text-[#D23A06] m-0 text-center"
            >
              {stopAndThinkTitle}
            </h2>
            {/* On the indeterminate-only path there are no positive reasons —
                show the generic message instead so the agent knows why they
                must still acknowledge (#498). */}
            <p
              id="stop-and-think-reasons"
              className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 leading-relaxed text-center"
            >
              {stopAndThinkTriggered ? (
                <>
                  <span className="font-bold">Reasons:</span>{' '}
                  {stopAndThinkReasons.join(', ')}
                </>
              ) : (
                stopAndThinkUnavailable
              )}
            </p>
            <div className="flex items-center gap-2">
              <Checkbox
                id="stop-and-think-ack"
                checked={stopAndThinkModalTicked}
                onCheckedChange={(v) => setStopAndThinkModalTicked(v === true)}
              />
              <label
                htmlFor="stop-and-think-ack"
                className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
              >
                {stopAndThinkAckLabel}
              </label>
            </div>
            <Button
              type="button"
              id="stop-and-think-ok"
              disabled={!stopAndThinkModalTicked || stopAndThinkAckPending}
              onClick={() => void acknowledgeStopAndThink()}
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-8 py-2 h-auto font-['Arial',sans-serif] font-bold text-[16px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              OK
            </Button>
          </div>
        </div>
      )}

      {/* Discreet Ardowen branding, bottom-right (issue #41). */}
      <ArdowenLogo />
    </main>
  );
}
